import React, { useCallback } from "react";
import { observer } from "mobx-react";
import { PageLayout } from "../../../../common/layout";
import { useRootStore } from "../../../../store/contexts";
import { PaymentReceiptCreateStore } from "../../store/PaymentReceiptCreateStore";
import { PaymentReceiptBatchCreateStore } from "../../store/PaymentReceiptBatchCreateStore";
import { PaymentReceiptBatchCreate } from "./PaymentReceiptBatchCreate";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { ErrorDialog } from "../../../../common/controls";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Paper,
  Alert,
} from "@mui/material";
import {
  CheckCircleIcon,
  CloudUploadIcon,
  DeleteIcon,
} from "../../../../icons";
import { useStore } from "../../../../hooks";

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const PaymentReceiptBatchCreatePage = observer(() => {
  const rootStore = useRootStore();
  const { langStore } = rootStore;
  const path = "/paymentReceipt/batch-create";

  const createStore = useStore(
    path + "/fields",
    () => new PaymentReceiptCreateStore(rootStore)
  );

  const batchStore = useStore(
    path + "/batch",
    () => new PaymentReceiptBatchCreateStore(rootStore)
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      const filesArray = Array.from(fileList);
      batchStore.addFiles(filesArray);
    },
    [batchStore]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleSubmit = () => {
    (batchStore as any).executeBatchCreate(createStore);
  };

  const handleClose = () => {
    rootStore.router.goBack();
  };

  const isProcessing =
    batchStore.status === "uploading" || batchStore.status === "creating";
  const isDone = batchStore.status === "done";

  return (
    <div>
      <PageLayout
        navigationElements={{
          links: createStore.navigationLinks,
          onNavigatedTo: (id: string) => {
            createStore.setCardState(id, true);
          },
        }}
        secondaryToolbox={
          <FormToolbox
            saveAndCloseLabel={langStore.batchCreateCommand}
            closeLabel={langStore.cancelNewEntity}
            saveForm={isProcessing || isDone ? undefined : handleSubmit}
            closeForm={handleClose}
          />
        }
      >
        {/* Shared fields form */}
        <PaymentReceiptBatchCreate
          paymentReceiptCreateStore={createStore}
          showToolbox={false}
        />

        {/* File upload zone */}
        <Box sx={{ mt: 3, px: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            {langStore.PaymentReceiptPaymentReceiptLabel || "PaymentReceipt"}
          </Typography>

          {!isDone && (
            <Paper
              variant="outlined"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                border: "2px dashed",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              onClick={() =>
                document.getElementById("batch-file-input")?.click()
              }
            >
              <CloudUploadIcon
                sx={{ fontSize: 48, color: "action.active", mb: 1 }}
              />
              <Typography>{langStore.batchCreateSelectFiles}</Typography>
              <input
                id="batch-file-input"
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
            </Paper>
          )}

          {/* File list */}
          {batchStore.files.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {batchStore.files.map((f, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" noWrap>
                      {f.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(f.size)}
                    </Typography>
                    {f.status === "uploading" && (
                      <LinearProgress sx={{ mt: 0.5 }} />
                    )}
                    {f.status === "error" && (
                      <Typography variant="caption" color="error">
                        {f.error}
                      </Typography>
                    )}
                  </Box>
                  {f.status === "uploaded" && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  {!isProcessing && !isDone && (
                    <IconButton
                      size="small"
                      onClick={() => batchStore.removeFile(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Paper>
              ))}
            </Box>
          )}

          {/* Status messages */}
          {batchStore.status === "uploading" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {langStore.batchCreateUploadingFiles}
            </Alert>
          )}
          {batchStore.status === "creating" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {langStore.batchCreateCreatingRecords}
            </Alert>
          )}
          {batchStore.status === "error" && batchStore.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {batchStore.error}
            </Alert>
          )}
          {isDone && batchStore.result && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {langStore.batchCreateSuccess(batchStore.result.created)}
            </Alert>
          )}

          {/* Confirmation text */}
          {batchStore.files.length > 0 && !isProcessing && !isDone && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {langStore.batchCreateConfirm(batchStore.files.length)}
            </Typography>
          )}
        </Box>
      </PageLayout>

      {/* Validation errors dialog (from CreateStore) */}
      <Dialog
        open={createStore.validationErrorsDialogOpen}
        onClose={() => createStore.closeValidationErrorsDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{langStore.validationErrorsTitle}</DialogTitle>
        <DialogContent>
          <Box>
            {Object.keys(createStore.errors).map((key) => {
              const error =
                createStore.errors[key as keyof typeof createStore.errors];
              if (!error) return null;
              return (
                <Typography
                  key={key}
                  color="error"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  • {error.fieldLabel}: {error.message}
                </Typography>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => createStore.closeValidationErrorsDialog()}
            color="primary"
          >
            {langStore.closeCommand}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error dialog (from CreateStore) */}
      <ErrorDialog
        open={createStore.errorDialogOpened}
        title={createStore.errorTitle}
        description={createStore.errorDescription}
        onClose={() => createStore.closeErrorDialog()}
      />
    </div>
  );
});
